'use client'

import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { EyeOff, VenetianMask } from 'lucide-react'
import { useBrandMask } from './shell/brandMask'

// Private columns: while the sidebar's demo mask is on (shell/brandMask.tsx),
// every table column that names who or what a report is about -- the
// beneficiary, the platform, the source, the brand -- shows its cells
// blurred. A column comes back with the mask button in its header; one
// entry comes back by clicking its blurred cell (all its private cells at
// once), and goes away again with the eye button on hover. Nothing is
// remembered: a reload starts from the sidebar's mode, which is the
// tf_brand_mask cookie (2026-10-23).
//
// ResizableTable does all of it on its own: it decides which of its columns
// are private and TableTd blurs itself when it sits in one -- the cell reads
// its own position (td.cellIndex), so no caller passes a column key. A
// hand-built <table> opts in with usePrivateColumns + PrivacyContext,
// PrivateColumnToggle in its header and PrivateTd for its cells (the Jobs
// page's handoffs table).

// By the column's key. Counts (unique_beneficiaries) and ids (platform_id)
// are not names and stay.
const PRIVATE_KEYS = /^(beneficiary|beneficiario|platform|platform_name|platform_namelong|platform_text|source|brand)$/

export type PrivateColumn = { key: string; private?: boolean }

export function isPrivateColumn(col: PrivateColumn): boolean {
  return col.private ?? PRIVATE_KEYS.test(col.key)
}

export type Privacy = {
  /** The sidebar's mask is on and this table has a private column. */
  active: boolean
  isPrivate: (colIdx: number) => boolean
  /** This column is blurred (it is private and not shown in full). */
  colHidden: (colIdx: number) => boolean
  toggleCol: (colIdx: number) => void
  rowRevealed: (tr: HTMLTableRowElement) => boolean
  toggleRow: (tr: HTMLTableRowElement) => void
}

const NONE: Privacy = {
  active: false,
  isPrivate: () => false,
  colHidden: () => false,
  toggleCol: () => {},
  rowRevealed: () => false,
  toggleRow: () => {},
}

export const PrivacyContext = createContext<Privacy>(NONE)

export function usePrivateColumns(columns: ReadonlyArray<PrivateColumn>): Privacy {
  const { masked } = useBrandMask()
  // Shown columns by key, so a table that drops columns on a narrow page
  // (Jobs history) keeps the choice; shown entries by their <tr>, so a new
  // page of rows starts hidden.
  const [shownCols, setShownCols] = useState<Set<string>>(() => new Set())
  const shownRows = useRef(new WeakSet<HTMLTableRowElement>())
  const [rowsVersion, setRowsVersion] = useState(0)
  useEffect(() => {
    setShownCols(new Set())
    shownRows.current = new WeakSet()
    setRowsVersion((v) => v + 1)
  }, [masked])

  const keys = columns.map((c) => (isPrivateColumn(c) ? c.key : null))
  const keySig = keys.join('|')
  const privateKeys = useMemo(() => keySig.split('|'), [keySig])
  const active = masked && privateKeys.some(Boolean)

  const toggleCol = useCallback((idx: number) => {
    const key = privateKeys[idx]
    if (!key) return
    setShownCols((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [privateKeys])
  const toggleRow = useCallback((tr: HTMLTableRowElement) => {
    if (shownRows.current.has(tr)) shownRows.current.delete(tr)
    else shownRows.current.add(tr)
    setRowsVersion((v) => v + 1)
  }, [])

  return useMemo(() => ({
    active,
    isPrivate: (idx: number) => active && !!privateKeys[idx],
    colHidden: (idx: number) => active && !!privateKeys[idx] && !shownCols.has(privateKeys[idx]),
    toggleCol,
    rowRevealed: (tr: HTMLTableRowElement) => shownRows.current.has(tr),
    toggleRow,
  // rowsVersion: a new value when an entry is shown or hidden, so cells re-render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [active, privateKeys, shownCols, toggleCol, toggleRow, rowsVersion])
}

/** The mask button in a private column's header (nothing when not active). */
export function PrivateColumnToggle({ colIdx, privacy }: { colIdx: number; privacy: Privacy }) {
  if (!privacy.isPrivate(colIdx)) return null
  const hidden = privacy.colHidden(colIdx)
  return (
    <button
      type="button"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); privacy.toggleCol(colIdx) }}
      title={hidden ? 'Show this column' : 'Hide this column'}
      aria-pressed={hidden}
      data-action="private-column"
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded transition-colors ${hidden ? 'text-signal hover:text-signal/80' : 'text-subtle/60 hover:text-subtle'}`}
    >
      <VenetianMask className="h-3 w-3" />
    </button>
  )
}

type TdProps = {
  children: React.ReactNode
  className?: string
  onClick?: React.MouseEventHandler<HTMLTableCellElement>
  title?: string
  style?: React.CSSProperties
  colSpan?: number
}

/** A <td> that blurs itself when it sits in a hidden private column. */
export function PrivateTd({ children, className = '', onClick, title, style, colSpan }: TdProps) {
  const privacy = useContext(PrivacyContext)
  const ref = useRef<HTMLTableCellElement>(null)
  // Its column, read before paint (a cell spanning columns belongs to none).
  const [colIdx, setColIdx] = useState<number | null>(null)
  useLayoutEffect(() => {
    const td = ref.current
    setColIdx(td && td.colSpan === 1 ? td.cellIndex : null)
  })
  const tr = ref.current?.parentElement instanceof HTMLTableRowElement ? ref.current.parentElement : null
  const isPrivate = colIdx != null && privacy.isPrivate(colIdx)
  const colHidden = isPrivate && privacy.colHidden(colIdx)
  const rowShown = colHidden && tr != null && privacy.rowRevealed(tr)
  const hidden = colHidden && !rowShown

  return (
    <td
      ref={ref}
      colSpan={colSpan}
      className={`${className} ${rowShown ? 'group/private relative' : ''} ${hidden ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      onClickCapture={hidden ? (e) => {
        // Shows this entry's private cells instead of opening the row.
        e.stopPropagation()
        e.preventDefault()
        if (tr) privacy.toggleRow(tr)
      } : undefined}
      title={hidden ? 'Hidden for the demo -- click to show this entry' : title}
      style={style}
      data-private={hidden ? 'hidden' : rowShown ? 'shown' : undefined}
    >
      {hidden ? (
        <span aria-hidden className="pointer-events-none block select-none blur-[5px]">{children}</span>
      ) : children}
      {rowShown && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); if (tr) privacy.toggleRow(tr) }}
          title="Hide this entry again"
          data-action="private-row"
          className="absolute right-1 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-[2px] bg-card text-subtle opacity-0 transition-opacity hover:text-foreground group-hover/private:opacity-100"
        >
          <EyeOff className="h-3 w-3" />
        </button>
      )}
    </td>
  )
}
